import { useEffect, useState, useCallback, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '@stores/auth.store';
import {
    getChat,
    sendMessage as sendMessageApi,
    sendMessageMultipart as sendMessageMultipartApi,
    editMessage as editMessageApi,
    deleteMessage as deleteMessageApi,
    getThreadReplies as getThreadRepliesApi,
    addReaction as addReactionApi,
    removeReaction as removeReactionApi,
    getEligibleChatUsers as getEligibleChatUsersApi,
    createChat as createChatApi,
    markChatAsRead as markChatAsReadApi,
    forwardMessages as forwardMessagesApi,
    updateThreadTitle as updateThreadTitleApi,
    searchChatMessages as searchChatMessagesApi,
    clearChatConversation as clearChatConversationApi,
    renameChat as renameChatApi,
    addChatMembers as addChatMembersApi,
    removeChatMember as removeChatMemberApi,
    leaveChat as leaveChatApi,
    getChatMessageAssets as getChatMessageAssetsApi,
} from '@services/chat.service';
import { CHAT_LIST_QUERY_KEY } from './useChatList';
import { getRealtimeSocket } from '@services/realtime.socket';

export const CHAT_QUERY_KEY = (chatId) => ['chat', 'detail', chatId];
export const CHAT_THREAD_QUERY_KEY = (chatId, parentId) => ['chat', 'thread', chatId, parentId];
export const CHAT_ELIGIBLE_USERS_QUERY_KEY = ['chat', 'eligible-users'];
export const CHAT_MESSAGE_SEARCH_QUERY_KEY = (chatId, term, page, limit) => ['chat', 'message-search', chatId, term, page, limit];
export const CHAT_MESSAGE_ASSETS_QUERY_KEY = (chatId, assetType, page, limit) => ['chat', 'message-assets', chatId, String(assetType), page, limit];

const CHAT_PAGE_LIMIT = 10;
/** Page size for GET /chat/:id/threads/:parentId (must match {@link getThreadReplies} default). */
export const THREAD_REPLY_PAGE_LIMIT = 30;
const MIN_LOADER_MS = 0;
const DEV = typeof import.meta !== 'undefined' && import.meta.env?.DEV;

function getMessageItemsFromResponse(data) {
    if (!data) return [];
    const messagesObj = data.messages ?? data.data?.messages;
    const list = messagesObj?.items ?? data.items ?? (Array.isArray(data.messages) ? data.messages : []);
    return Array.isArray(list) ? list : [];
}

function getMessageTimeMs(m) {
    const ts = m?.createdAt ?? m?.created_at ?? m?.updatedAt ?? m?.updated_at ?? 0;
    const t = typeof ts === 'number' ? ts : new Date(ts || 0).getTime();
    return Number.isFinite(t) ? t : 0;
}

function normalizeMessageItems(items) {
    const arr = Array.isArray(items) ? items.slice() : [];
    const seen = new Set();
    const deduped = [];
    for (const m of arr) {
        const id = m?.id ?? m?._id;
        if (!id) continue;
        const key = String(id);
        if (seen.has(key)) continue;
        seen.add(key);
        deduped.push(m);
    }
    // Always keep ascending order so the UI doesn't need to sort large arrays during render.
    deduped.sort((a, b) => getMessageTimeMs(a) - getMessageTimeMs(b));
    return deduped;
}

/** Thread reply APIs often omit parent id (implicit from route). Stamp so edit/socket logic can treat replies as threaded. */
function stampThreadParentOnItems(items, parentId) {
    if (!parentId || !Array.isArray(items)) return items;
    const pid = String(parentId);
    return items.map((m) => {
        const existing = m?.parentMessageId ?? m?.parent_message_id;
        if (existing != null && String(existing) !== '') return m;
        return { ...m, parentMessageId: pid };
    });
}

export const useChat = (chatId) => {
    const queryClient = useQueryClient();
    const [isFetchingNextPage, setIsFetchingNextPage] = useState(false);

    const query = useQuery({
        queryKey: CHAT_QUERY_KEY(chatId),
        queryFn: () => getChat(chatId, { page: 1, limit: CHAT_PAGE_LIMIT }),
        enabled: !!chatId,
        // Keep previous chat payload while switching / refetching to avoid UI "blanking" and perceived freezes.
        placeholderData: (prev) => prev,
        select: (raw) => {
            if (!raw) return raw;
            const messagesObj = raw.messages ?? {};
            const items = normalizeMessageItems(getMessageItemsFromResponse(raw));
            return { ...raw, messages: { ...messagesObj, items } };
        },
    });

    const data = query.data;
    const messagesPayload = data?.messages ?? {};
    const currentPage = messagesPayload.page ?? 1;
    const totalPages = messagesPayload.totalPages ?? 1;
    const limit = messagesPayload.limit ?? CHAT_PAGE_LIMIT;
    const hasNextPage = currentPage < totalPages;

    const fetchNextPage = useCallback(async () => {
        if (!chatId || !hasNextPage || isFetchingNextPage) return;
        setIsFetchingNextPage(true);
        const start = Date.now();
        try {
            const nextPage = currentPage + 1;
            if (DEV) console.debug('[chat] pagination start', { chatId, nextPage, limit });
            const res = await getChat(chatId, { page: nextPage, limit });
            const elapsed = Date.now() - start;
            if (elapsed < MIN_LOADER_MS) {
                await new Promise((r) => setTimeout(r, MIN_LOADER_MS - elapsed));
            }
            const newItems = normalizeMessageItems(getMessageItemsFromResponse(res));
            queryClient.setQueryData(CHAT_QUERY_KEY(chatId), (prev) => {
                if (!prev) return prev;
                const prevItems = normalizeMessageItems(getMessageItemsFromResponse(prev));
                const seen = new Set(prevItems.map((m) => String(m.id ?? m._id)));
                const fromNew = newItems.filter((m) => !seen.has(String(m.id ?? m._id)));
                // Older pages should be older timestamps; we keep asc order by concatenating then normalizing once.
                const merged = normalizeMessageItems([...fromNew, ...prevItems]);
                const messages = prev.messages ?? {};
                return {
                    ...prev,
                    messages: { ...messages, ...(res.messages ?? {}), items: merged, page: nextPage },
                };
            });
            if (DEV) console.debug('[chat] pagination complete', { chatId, nextPage, added: newItems.length, elapsedMs: Date.now() - start });
        } finally {
            setIsFetchingNextPage(false);
        }
    }, [chatId, hasNextPage, isFetchingNextPage, currentPage, limit, queryClient]);

    return {
        ...query,
        hasNextPage,
        fetchNextPage,
        isFetchingNextPage,
    };
};

export const useChatMessageSearch = ({ chatId, term, page = 1, limit = 20, enabled = true }) => {
    return useQuery({
        queryKey: CHAT_MESSAGE_SEARCH_QUERY_KEY(chatId, term, page, limit),
        queryFn: () => searchChatMessagesApi({ chatId, term, page, limit }),
        enabled: !!enabled && !!chatId && !!String(term || '').trim(),
        placeholderData: (prev) => prev,
    });
};

/**
 * Paginated GET /chat/:chatId/messages/assets for one category.
 * @param {string} assetType — `media` | `docs` | `links` (query `type` sent to the API)
 */
export const useChatMessageAssets = ({ chatId, assetType, page = 1, limit = 50, enabled = true }) => {
    return useQuery({
        queryKey: CHAT_MESSAGE_ASSETS_QUERY_KEY(chatId, assetType, page, limit),
        queryFn: () => getChatMessageAssetsApi(chatId, { type: assetType, page, limit }),
        enabled: Boolean(enabled && chatId && assetType),
        placeholderData: (prev) => prev,
    });
};

export const useClearChatConversation = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (chatId) => clearChatConversationApi(chatId),
        onMutate: async (chatId) => {
            // Optimistic: clear messages immediately for snappy UX.
            queryClient.setQueryData(CHAT_QUERY_KEY(chatId), (prev) => {
                if (!prev) return prev;
                const messages = prev.messages ?? {};
                return {
                    ...prev,
                    messages: { ...messages, items: [], total: 0, page: 1, totalPages: 1 },
                };
            });
            queryClient.setQueriesData({ queryKey: CHAT_LIST_QUERY_KEY }, (listData) => {
                if (!listData?.items) return listData;
                const items = Array.isArray(listData.items) ? listData.items : [];
                return {
                    ...listData,
                    items: items.map((c) => {
                        const cid = c.id ?? c._id;
                        if (String(cid) !== String(chatId)) return c;
                        return { ...c, unreadCount: 0, lastMessage: null };
                    }),
                };
            });
        },
        onError: (_err, _chatId) => {
            // Restore truth on failure.
            queryClient.invalidateQueries({ queryKey: CHAT_LIST_QUERY_KEY });
        },
        onSuccess: (_data, chatId) => {
            // Ensure detail + list match backend truth.
            queryClient.invalidateQueries({ queryKey: CHAT_QUERY_KEY(chatId) });
            queryClient.invalidateQueries({ queryKey: CHAT_LIST_QUERY_KEY });
        },
    });
};

function getChatOwnerId(chat) {
    return (
        chat?.createdById ??
        chat?.createdBy ??
        chat?.ownerId ??
        chat?.owner_id ??
        chat?.adminId ??
        chat?.admin_id ??
        chat?.initiatorId ??
        chat?.initiator_id ??
        null
    );
}

export function canManageGroupChat(chat, currentUserId) {
    const uid = currentUserId ?? null;
    if (!uid || !chat) return false;
    const ownerId = getChatOwnerId(chat);
    if (ownerId && String(ownerId) === String(uid)) return true;
    const parts = Array.isArray(chat?.participants) ? chat.participants : [];
    const me = parts.find((p) => String(p?.userId ?? p?.id) === String(uid));
    if (me?.role && String(me.role).toLowerCase() === 'owner') return true;
    if (me?.isOwner === true || me?.isAdmin === true || me?.admin === true) return true;
    return false;
}

export const useRenameChat = (chatId) => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ name }) => renameChatApi(chatId, name),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: CHAT_QUERY_KEY(chatId) });
            queryClient.invalidateQueries({ queryKey: CHAT_LIST_QUERY_KEY });
        },
    });
};

export const useAddChatMembers = (chatId) => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ memberIds }) => addChatMembersApi(chatId, memberIds),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: CHAT_QUERY_KEY(chatId) });
            queryClient.invalidateQueries({ queryKey: CHAT_LIST_QUERY_KEY });
        },
    });
};

export const useRemoveChatMember = (chatId) => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ userId }) => removeChatMemberApi(chatId, userId),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: CHAT_QUERY_KEY(chatId) });
            queryClient.invalidateQueries({ queryKey: CHAT_LIST_QUERY_KEY });
        },
    });
};

export const useLeaveChat = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (chatId) => leaveChatApi(chatId),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: CHAT_LIST_QUERY_KEY });
        },
    });
};

/** Append a thread reply to the thread query cache (keeps loaded pages + scroll position). */
export function appendMessageToThreadCache(queryClient, chatId, parentMessageId, message) {
    if (!message || !chatId || !parentMessageId) return;
    const pid = String(parentMessageId);
    const stamped = {
        ...message,
        parentMessageId: message?.parentMessageId ?? message?.parent_message_id ?? pid,
    };
    queryClient.setQueryData(CHAT_THREAD_QUERY_KEY(chatId, parentMessageId), (prev) => {
        if (!prev || !Array.isArray(prev.items)) return prev;
        const items = prev.items;
        const id = stamped.id ?? stamped._id;
        if (id && items.some((m) => String(m.id ?? m._id) === String(id))) return prev;
        const nextItems = normalizeMessageItems([...items, stamped]);
        const prevTotal = typeof prev.total === 'number' ? prev.total : items.length;
        return {
            ...prev,
            items: nextItems,
            total: prevTotal + 1,
        };
    });
}

/** Merge an edited message into a loaded thread cache (same attachment preservation as main chat). */
export function mergeMessageIntoThreadCache(queryClient, chatId, parentMessageId, updated) {
    if (!queryClient || !chatId || !parentMessageId || !updated) return;
    const updatedId = updated?.id ?? updated?._id;
    if (!updatedId) return;
    queryClient.setQueryData(CHAT_THREAD_QUERY_KEY(chatId, parentMessageId), (prev) => {
        if (!prev?.items) return prev;
        const items = prev.items;
        const has = items.some((m) => String(m.id ?? m._id) === String(updatedId));
        if (!has) return prev;
        const nextItems = items.map((m) => {
            const mid = m?.id ?? m?._id;
            if (String(mid) !== String(updatedId)) return m;
            const prevAttachments = m?.content?.attachments ?? m?.attachments;
            const next = { ...m, ...updated };
            const nextContent = { ...(m?.content ?? {}), ...(updated?.content ?? {}) };
            const nextAttachmentsField = nextContent?.attachments;
            if (
                nextContent &&
                (nextAttachmentsField == null ||
                    (Array.isArray(nextAttachmentsField) &&
                        nextAttachmentsField.length === 0 &&
                        Array.isArray(prevAttachments) &&
                        prevAttachments.length > 0))
            ) {
                nextContent.attachments = prevAttachments;
            }
            next.content = nextContent;
            const topNext = next?.attachments;
            if (
                topNext == null ||
                (Array.isArray(topNext) &&
                    topNext.length === 0 &&
                    Array.isArray(prevAttachments) &&
                    prevAttachments.length > 0)
            ) {
                next.attachments = prevAttachments;
            }
            next.parentMessageId =
                updated?.parentMessageId ??
                updated?.parent_message_id ??
                m?.parentMessageId ??
                m?.parent_message_id ??
                String(parentMessageId);
            return next;
        });
        return { ...prev, items: normalizeMessageItems(nextItems) };
    });
}

/** Invalidate all thread reply queries for a chat (e.g. delete / reaction refresh). */
export function invalidateThreadCachesForChat(queryClient, chatId) {
    if (!queryClient || !chatId) return;
    queryClient.invalidateQueries({ queryKey: ['chat', 'thread', chatId] });
}

/** Append a message to the chat query cache; bump parent `replyCount` when this is a thread reply. */
export function appendMessageToChatCache(queryClient, chatId, message) {
    if (!message || !chatId) return;
    queryClient.setQueryData(CHAT_QUERY_KEY(chatId), (prev) => {
        if (!prev) return prev;
        const messages = prev.messages ?? {};
        const items = Array.isArray(messages.items) ? messages.items : [];
        const id = message.id ?? message._id;
        if (id && items.some((m) => (m.id ?? m._id) === id)) return prev;
        let nextItems = [...items, message];
        const parentId = message?.parentMessageId ?? message?.parent_message_id;
        if (parentId) {
            const pid = String(parentId);
            const childTs = message.createdAt ?? message.created_at ?? null;
            nextItems = nextItems.map((m) => {
                if (String(m.id ?? m._id) !== pid) return m;
                const rc = Number(m.replyCount ?? m.reply_count ?? 0);
                const nextRc = (Number.isFinite(rc) ? rc : 0) + 1;
                const prevLast = m.lastReplyAt ?? m.last_reply_at ?? null;
                let lastReplyAt = prevLast;
                if (childTs && (!prevLast || new Date(childTs) > new Date(prevLast))) {
                    lastReplyAt = childTs;
                }
                return {
                    ...m,
                    replyCount: nextRc,
                    reply_count: nextRc,
                    ...(lastReplyAt ? { lastReplyAt, last_reply_at: lastReplyAt } : {}),
                };
            });
        }
        return { ...prev, messages: { ...messages, items: nextItems } };
    });
}

/** Update chat list cache: set lastMessage for the given chatId. */
function updateChatListCache(queryClient, chatId, message) {
    if (!message || !chatId) return;
    queryClient.setQueriesData({ queryKey: CHAT_LIST_QUERY_KEY }, (listData) => {
        if (!listData?.items) return listData;
        return {
            ...listData,
            items: listData.items.map((chat) => {
                const cid = chat.id ?? chat._id;
                if (String(cid) !== String(chatId)) return chat;
                return { ...chat, lastMessage: message, updatedAt: message.createdAt ?? message.created_at ?? new Date().toISOString() };
            }),
        };
    });
}

/** Send message; pass { text, parentMessageId?, files? }. Uses multipart when files present (type: media or voice for single audio). */
export const useSendMessage = (chatId) => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ text, parentMessageId, files, linkAttachments }) => {
            const hasFiles = Array.isArray(files) && files.length > 0;
            const attachmentUrls = Array.isArray(linkAttachments) ? linkAttachments.map((a) => ({ url: a.url, title: a.title || a.url })) : [];
            if (hasFiles) {
                const content = { type: 'markdown', text: (text || '').trim(), mentions: [], hashtagRefs: [] };
                const type =
                    files.length === 1 && (files[0].type || '').startsWith('audio/') ? 'voice' : 'media';
                return sendMessageMultipartApi(chatId, {
                    content,
                    type,
                    parentMessageId: parentMessageId || undefined,
                    attachmentUrls: attachmentUrls.length > 0 ? attachmentUrls : undefined,
                    files,
                });
            }
            return sendMessageApi(chatId, text, {
                parentMessageId: parentMessageId || undefined,
                attachments: attachmentUrls.length > 0 ? attachmentUrls : undefined,
            });
        },
        onSuccess: (data, { parentMessageId }) => {
            const message = data?.message ?? data?.data ?? data;
            if (message && (message.id ?? message._id)) {
                appendMessageToChatCache(queryClient, chatId, message);
                if (!parentMessageId) updateChatListCache(queryClient, chatId, message);
            }
            if (parentMessageId) {
                const tKey = CHAT_THREAD_QUERY_KEY(chatId, parentMessageId);
                if (queryClient.getQueryData(tKey)) {
                    appendMessageToThreadCache(queryClient, chatId, parentMessageId, message);
                } else {
                    queryClient.invalidateQueries({ queryKey: tKey });
                }
            }
        },
    });
};

export const useEditMessage = (chatId) => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ msgId, text }) => editMessageApi(chatId, msgId, text),
        onSuccess: (data, { msgId }) => {
            const updated = data?.message ?? data?.data ?? data;
            const updatedId = updated?.id ?? updated?._id ?? msgId;
            if (!updatedId) return;

            // Preserve attachments if backend doesn't include them in edit response.
            queryClient.setQueryData(CHAT_QUERY_KEY(chatId), (prev) => {
                if (!prev) return prev;
                const messages = prev.messages ?? {};
                const items = Array.isArray(messages.items) ? messages.items : [];
                const nextItems = items.map((m) => {
                    const mid = m?.id ?? m?._id;
                    if (String(mid) !== String(updatedId)) return m;
                    const prevAttachments = m?.content?.attachments ?? m?.attachments;
                    const next = { ...m, ...updated };

                    const nextContent = { ...(m?.content ?? {}), ...(updated?.content ?? {}) };
                    // Edits are text-only; attachments are immutable. Some APIs return attachments: [] on edit.
                    // Treat "missing OR empty array" as "unchanged" and preserve previous attachments.
                    const nextAttachmentsField = nextContent?.attachments;
                    if (
                        nextContent &&
                        (nextAttachmentsField == null ||
                            (Array.isArray(nextAttachmentsField) &&
                                nextAttachmentsField.length === 0 &&
                                Array.isArray(prevAttachments) &&
                                prevAttachments.length > 0))
                    ) {
                        nextContent.attachments = prevAttachments;
                    }
                    next.content = nextContent;

                    const topNext = next?.attachments;
                    if (
                        topNext == null ||
                        (Array.isArray(topNext) &&
                            topNext.length === 0 &&
                            Array.isArray(prevAttachments) &&
                            prevAttachments.length > 0)
                    ) {
                        next.attachments = prevAttachments;
                    }
                    return next;
                });
                return { ...prev, messages: { ...messages, items: nextItems } };
            });

            // Update chat list preview if it references this message.
            queryClient.setQueriesData({ queryKey: CHAT_LIST_QUERY_KEY }, (listData) => {
                if (!listData?.items) return listData;
                return {
                    ...listData,
                    items: listData.items.map((c) => {
                        const last = c?.lastMessage ?? null;
                        const lid = last?.id ?? last?._id;
                        if (String(lid) !== String(updatedId)) return c;
                        const prevAttachments = last?.content?.attachments ?? last?.attachments;
                        const merged = { ...last, ...updated };
                        const mergedContent = { ...(last?.content ?? {}), ...(updated?.content ?? {}) };
                        const nextAttachmentsField = mergedContent?.attachments;
                        if (
                            mergedContent &&
                            (nextAttachmentsField == null ||
                                (Array.isArray(nextAttachmentsField) &&
                                    nextAttachmentsField.length === 0 &&
                                    Array.isArray(prevAttachments) &&
                                    prevAttachments.length > 0))
                        ) {
                            mergedContent.attachments = prevAttachments;
                        }
                        merged.content = mergedContent;
                        const topNext = merged?.attachments;
                        if (
                            topNext == null ||
                            (Array.isArray(topNext) &&
                                topNext.length === 0 &&
                                Array.isArray(prevAttachments) &&
                                prevAttachments.length > 0)
                        ) {
                            merged.attachments = prevAttachments;
                        }
                        return { ...c, lastMessage: merged };
                    }),
                };
            });

            const parentId = updated?.parentMessageId ?? updated?.parent_message_id;
            if (parentId && chatId) {
                mergeMessageIntoThreadCache(queryClient, chatId, String(parentId), updated);
            }
        },
    });
};

export const useDeleteMessage = (chatId) => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (msgId) => deleteMessageApi(chatId, msgId),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: CHAT_QUERY_KEY(chatId) });
            queryClient.invalidateQueries({ queryKey: CHAT_LIST_QUERY_KEY });
            invalidateThreadCachesForChat(queryClient, chatId);
        },
    });
};

export const useThreadReplies = (chatId, parentId) => {
    const queryClient = useQueryClient();
    const [isFetchingNextPage, setIsFetchingNextPage] = useState(false);

    const query = useQuery({
        queryKey: CHAT_THREAD_QUERY_KEY(chatId, parentId),
        queryFn: async () => {
            const res = await getThreadRepliesApi(chatId, parentId, { page: 1, limit: THREAD_REPLY_PAGE_LIMIT });
            return {
                ...res,
                items: normalizeMessageItems(stampThreadParentOnItems(res.items ?? [], parentId)),
            };
        },
        enabled: !!chatId && !!parentId,
        // Do not keep previous thread data while `parentId` changes — it was bleeding into
        // `threadReplies.length` and overwriting `threadCountOverlay` with the wrong reply count.
    });

    const data = query.data;
    const currentPage = data?.page ?? 1;
    const totalPages = data?.totalPages ?? 1;
    const hasNextPage = currentPage < totalPages;

    const fetchNextPage = useCallback(async () => {
        if (!chatId || !parentId || !hasNextPage || isFetchingNextPage) return;
        const key = CHAT_THREAD_QUERY_KEY(chatId, parentId);
        const prevSnap = queryClient.getQueryData(key);
        const page = prevSnap?.page ?? 1;
        const limit = prevSnap?.limit ?? THREAD_REPLY_PAGE_LIMIT;
        if (page >= (prevSnap?.totalPages ?? 1)) return;

        setIsFetchingNextPage(true);
        const start = Date.now();
        try {
            const nextPage = page + 1;
            const res = await getThreadRepliesApi(chatId, parentId, { page: nextPage, limit });
            const elapsed = Date.now() - start;
            if (elapsed < MIN_LOADER_MS) {
                await new Promise((r) => setTimeout(r, MIN_LOADER_MS - elapsed));
            }
            const newItems = normalizeMessageItems(stampThreadParentOnItems(res.items ?? [], parentId));
            queryClient.setQueryData(key, (prev) => {
                if (!prev) return prev;
                const prevItems = Array.isArray(prev.items) ? prev.items : [];
                const seen = new Set(prevItems.map((m) => String(m.id ?? m._id)));
                const fromNew = newItems.filter((m) => !seen.has(String(m.id ?? m._id)));
                const merged = normalizeMessageItems([...fromNew, ...prevItems]);
                return {
                    ...prev,
                    ...res,
                    items: merged,
                    page: res.page ?? nextPage,
                    totalPages: res.totalPages ?? prev.totalPages,
                    total: typeof res.total === 'number' ? res.total : prev.total,
                    limit: res.limit ?? prev.limit ?? limit,
                };
            });
        } finally {
            setIsFetchingNextPage(false);
        }
    }, [chatId, parentId, hasNextPage, isFetchingNextPage, queryClient]);

    return {
        ...query,
        hasNextPage,
        fetchNextPage,
        isFetchingNextPage,
    };
};

export const useAddReaction = (chatId) => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ messageId, emoji }) => addReactionApi(chatId, messageId, emoji),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: CHAT_QUERY_KEY(chatId) });
        },
    });
};

export const useRemoveReaction = (chatId) => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ messageId, emoji }) => removeReactionApi(chatId, messageId, emoji),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: CHAT_QUERY_KEY(chatId) });
        },
    });
};

export const useForwardMessages = (chatId) => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ messageIds, targetChatIds, targetUserIds }) => {
            const ids = Array.isArray(messageIds) ? messageIds.filter(Boolean) : [];
            if (!chatId || ids.length === 0) return Promise.resolve(null);
            const body = {
                ...(Array.isArray(targetChatIds) && targetChatIds.length > 0 ? { targetChatIds } : {}),
                ...(Array.isArray(targetUserIds) && targetUserIds.length > 0 ? { targetUserIds } : {}),
            };
            // Backend endpoint forwards ONE message (msgId in URL). For multi-select, call once per message.
            return Promise.all(ids.map((msgId) => forwardMessagesApi(chatId, msgId, body)));
        },
        onSuccess: () => {
            // Forwarding can create/refresh chats; safest to refresh list.
            queryClient.invalidateQueries({ queryKey: CHAT_LIST_QUERY_KEY });
        },
    });
};

export const useUpdateThreadTitle = (chatId) => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ msgId, threadTitle }) => updateThreadTitleApi(chatId, msgId, threadTitle),
        onSuccess: (_data, vars) => {
            const { msgId, threadTitle } = vars ?? {};
            if (!msgId) return;
            queryClient.setQueryData(CHAT_QUERY_KEY(chatId), (prev) => {
                if (!prev) return prev;
                const messagesObj = prev.messages ?? {};
                const items = Array.isArray(messagesObj.items) ? messagesObj.items : [];
                const nextItems = items.map((m) => {
                    const mid = m.id ?? m._id;
                    if (String(mid) !== String(msgId)) return m;
                    return { ...m, threadTitle };
                });
                return { ...prev, messages: { ...messagesObj, items: nextItems } };
            });
        },
    });
};

export const useEligibleChatUsers = (params = {}) => {
    const { enabled = true, ...rest } = params;
    return useQuery({
        queryKey: [...CHAT_ELIGIBLE_USERS_QUERY_KEY, rest],
        queryFn: () => getEligibleChatUsersApi(rest),
        enabled: !!enabled,
    });
};

export const useCreateChat = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ type, participantIds, name }) => createChatApi({ type, participantIds, ...(name ? { name } : {}) }),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: CHAT_LIST_QUERY_KEY });
        },
    });
};

export const useMarkChatAsRead = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (chatId) => markChatAsReadApi(chatId),
        onMutate: async (chatId) => {
            // Optimistically clear unread badge for this chat.
            queryClient.setQueriesData({ queryKey: CHAT_LIST_QUERY_KEY }, (listData) => {
                if (!listData?.items) return listData;
                const items = Array.isArray(listData.items) ? listData.items : [];
                return {
                    ...listData,
                    items: items.map((c) => {
                        const cid = c.id ?? c._id;
                        if (String(cid) !== String(chatId)) return c;
                        return { ...c, unreadCount: 0 };
                    }),
                };
            });
        },
        onError: () => {
            // On error, refresh list to restore truth.
            queryClient.invalidateQueries({ queryKey: CHAT_LIST_QUERY_KEY });
        },
        onSuccess: (data, chatId) => {
            // Response shape (per backend docs): { chatId, lastReadAt, unreadCount }
            const payload = data?.data ?? data;
            const unreadCount =
                typeof payload?.unreadCount === 'number' ? payload.unreadCount : 0;
            const lastReadAt = payload?.lastReadAt ?? null;

            queryClient.setQueriesData({ queryKey: CHAT_LIST_QUERY_KEY }, (listData) => {
                if (!listData?.items) return listData;
                const items = Array.isArray(listData.items) ? listData.items : [];
                return {
                    ...listData,
                    items: items.map((c) => {
                        const cid = c.id ?? c._id;
                        if (String(cid) !== String(chatId)) return c;
                        return {
                            ...c,
                            unreadCount,
                            ...(lastReadAt ? { lastReadAt } : {}),
                        };
                    }),
                };
            });
        },
    });
};

/**
 * Mark ALL chats as read for current user.
 * Backend emits `chat:unread:updated` per chat; we optimistically clear the badges immediately.
 *
 * Note: there is no documented "read-all" chat endpoint, so we call read per chatId.
 */
export const useMarkAllChatsAsRead = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (chatIds) => {
            const ids = Array.isArray(chatIds) ? chatIds.filter(Boolean) : [];
            if (ids.length === 0) return [];
            const results = await Promise.allSettled(ids.map((id) => markChatAsReadApi(id)));
            return results;
        },
        onMutate: async () => {
            // Optimistically clear unread badges for all chats.
            queryClient.setQueriesData({ queryKey: CHAT_LIST_QUERY_KEY }, (listData) => {
                if (!listData?.items) return listData;
                const items = Array.isArray(listData.items) ? listData.items : [];
                return {
                    ...listData,
                    items: items.map((c) => ({ ...c, unreadCount: 0 })),
                };
            });
        },
        onError: () => {
            // On error, refresh list to restore truth.
            queryClient.invalidateQueries({ queryKey: CHAT_LIST_QUERY_KEY });
        },
        onSuccess: () => {
            // Ensure everything is consistent after individual calls return.
            queryClient.invalidateQueries({ queryKey: CHAT_LIST_QUERY_KEY });
        },
    });
};

/**
 * Automatically mark a chat as read once when the user is inside it.
 * This covers cases like opening via deep link or realtime-created DM/group,
 * not only clicking from the sidebar.
 */
export const useAutoMarkChatAsRead = (chatId) => {
    const markRead = useMarkChatAsRead();
    const lastMarkedRef = useRef(null);
    const lastMarkedAtRef = useRef(0);

    useEffect(() => {
        if (!chatId) return;
        if (String(lastMarkedRef.current) === String(chatId)) return;
        lastMarkedRef.current = chatId;
        lastMarkedAtRef.current = Date.now();
        markRead.mutate(chatId);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [chatId]);
};

/**
 * Mark a chat as read whenever the chat is open and new messages arrive.
 * This prevents the unread badge from getting "stuck" at 1 due to socket unread updates
 * while the user is actively viewing the conversation.
 */
export const useAutoMarkChatAsReadOnActivity = (chatId, activitySignal) => {
    const markRead = useMarkChatAsRead();
    const lastChatRef = useRef(null);
    const lastMarkedAtRef = useRef(0);

    useEffect(() => {
        if (!chatId) return;
        const now = Date.now();
        const isSameChat = String(lastChatRef.current) === String(chatId);
        const isHidden = typeof document !== 'undefined' ? document.hidden : false;

        lastChatRef.current = chatId;
        if (isHidden) return;

        // Throttle to avoid spamming when multiple messages render quickly.
        const THROTTLE_MS = 1200;
        if (isSameChat && now - lastMarkedAtRef.current < THROTTLE_MS) return;

        lastMarkedAtRef.current = now;
        markRead.mutate(chatId);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [chatId, activitySignal]);
};

/**
 * Listen for inbox-level chat events (no join-chat required).
 * Server auto-joins you to user:{userId} on connect.
 *
 * - chat:inbox:new → reorder list, update lastMessage preview
 * - chat:unread:updated → update unreadCount badge for one chat
 */
export function useChatInboxSocket() {
    const queryClient = useQueryClient();

    useEffect(() => {
        const s = getRealtimeSocket();
        if (!s) return;
        const DEV = typeof import.meta !== 'undefined' && import.meta.env?.DEV;
        const currentUserId = useAuthStore.getState().user?.id ?? null;

        const getActiveChatIdFromPath = () => {
            try {
                const path = String(window?.location?.pathname ?? '');
                // Matches /chat/:chatId or /tenant/chat/:chatId
                const m = path.match(/(?:^|\/)(?:tenant\/)?chat\/([^/?#]+)/i);
                return m?.[1] ?? null;
            } catch {
                return null;
            }
        };

        const onInboxNew = (payload) => {
            const chatId = payload?.chatId;
            if (!chatId) return;

            const previewText = payload?.preview ?? '';
            const createdAt = payload?.createdAt ?? new Date().toISOString();
            const senderId = payload?.senderId ?? null;
            const messageId = payload?.messageId ?? null;
            const type = payload?.type ?? 'text';

            const syntheticLastMessage = {
                id: messageId,
                type,
                createdAt,
                senderId,
                content: { type: 'markdown', text: previewText, mentions: [], hashtagRefs: [] },
            };

            queryClient.setQueriesData({ queryKey: CHAT_LIST_QUERY_KEY }, (listData) => {
                if (!listData?.items) return listData;
                const items = Array.isArray(listData.items) ? listData.items : [];
                const idx = items.findIndex((c) => String(c.id ?? c._id) === String(chatId));
                if (idx === -1) {
                    // Chat may not be in cache yet (new DM/group created elsewhere) – refetch list.
                    queryClient.invalidateQueries({ queryKey: CHAT_LIST_QUERY_KEY });
                    return listData;
                }
                const target = items[idx];
                const updated = {
                    ...target,
                    lastMessage: { ...(target.lastMessage ?? {}), ...syntheticLastMessage },
                    updatedAt: createdAt,
                };
                const next = [updated, ...items.filter((_, i) => i !== idx)];
                return { ...listData, items: next };
            });
        };

        const onUnreadUpdated = (payload) => {
            const chatId = payload?.chatId;
            if (!chatId) return;
            const unreadCount = payload?.unreadCount;
            const activeChatId = getActiveChatIdFromPath();
            const isActiveChat = activeChatId && String(activeChatId) === String(chatId);
            queryClient.setQueriesData({ queryKey: CHAT_LIST_QUERY_KEY }, (listData) => {
                if (!listData?.items) return listData;
                const items = Array.isArray(listData.items) ? listData.items : [];
                return {
                    ...listData,
                    items: items.map((c) => {
                        const cid = c.id ?? c._id;
                        if (String(cid) !== String(chatId)) return c;
                        // If user is currently viewing the chat, never show unread badge.
                        if (isActiveChat) return { ...c, unreadCount: 0 };
                        return { ...c, unreadCount: typeof unreadCount === 'number' ? unreadCount : c.unreadCount };
                    }),
                };
            });
        };

        // Some backend flows emit this when a chat is explicitly marked read (single or bulk).
        // Treat it as authoritative and clear unread for that chat.
        const onReadUpdated = (payload) => {
            const chatId = payload?.chatId;
            if (!chatId) return;
            const lastReadAt = payload?.lastReadAt ?? payload?.readAt ?? null;
            queryClient.setQueriesData({ queryKey: CHAT_LIST_QUERY_KEY }, (listData) => {
                if (!listData?.items) return listData;
                const items = Array.isArray(listData.items) ? listData.items : [];
                return {
                    ...listData,
                    items: items.map((c) => {
                        const cid = c.id ?? c._id;
                        if (String(cid) !== String(chatId)) return c;
                        return {
                            ...c,
                            unreadCount: 0,
                            ...(lastReadAt ? { lastReadAt } : {}),
                        };
                    }),
                };
            });
        };

        const onChatCleared = (payload) => {
            const chatId = payload?.chatId ?? payload?.id ?? null;
            if (!chatId) return;
            // Clear chat detail cache messages.
            queryClient.setQueryData(CHAT_QUERY_KEY(chatId), (prev) => {
                if (!prev) return prev;
                const messages = prev.messages ?? {};
                return { ...prev, messages: { ...messages, items: [], total: 0, page: 1, totalPages: 1 } };
            });
            // Clear sidebar preview/unread.
            queryClient.setQueriesData({ queryKey: CHAT_LIST_QUERY_KEY }, (listData) => {
                if (!listData?.items) return listData;
                const items = Array.isArray(listData.items) ? listData.items : [];
                return {
                    ...listData,
                    items: items.map((c) => {
                        const cid = c.id ?? c._id;
                        if (String(cid) !== String(chatId)) return c;
                        return { ...c, unreadCount: 0, lastMessage: null };
                    }),
                };
            });
        };

        const removeChatFromCurrentUser = (chatId) => {
            if (!chatId) return;
            queryClient.setQueriesData({ queryKey: CHAT_LIST_QUERY_KEY }, (listData) => {
                if (!listData?.items) return listData;
                const items = Array.isArray(listData.items) ? listData.items : [];
                return { ...listData, items: items.filter((c) => String(c.id ?? c._id) !== String(chatId)) };
            });
            queryClient.removeQueries({ queryKey: CHAT_QUERY_KEY(chatId) });
            try {
                const activeChatId = getActiveChatIdFromPath();
                if (activeChatId && String(activeChatId) === String(chatId)) {
                    window.dispatchEvent(new CustomEvent('chat:exited', { detail: { chatId } }));
                }
            } catch {
                // ignore
            }
        };

        const onGroupChanged = (payload) => {
            const chatId = payload?.chatId ?? payload?.id ?? null;
            if (!chatId) return;

            if (DEV) {
                const ev = payload?.event ?? payload?.type ?? payload?.action ?? 'group:event';
                // eslint-disable-next-line no-console
                console.debug('[chat] group event', ev, { chatId, payload });
            }

            // If THIS user was removed from the group, drop it from their list immediately.
            const affectedUserId =
                payload?.memberId ??
                payload?.member_id ??
                payload?.removedUserId ??
                payload?.removed_user_id ??
                payload?.userId ??
                payload?.user_id ??
                null;
            const action =
                String(payload?.action ?? payload?.event ?? '').toLowerCase() ||
                String(payload?.type ?? '').toLowerCase();
            const isSelfTargetedRemoval =
                currentUserId &&
                affectedUserId &&
                String(affectedUserId) === String(currentUserId) &&
                (action.includes('removed') || action.includes('remove') || action.includes('exited') || action.includes('left'));
            if (isSelfTargetedRemoval) {
                removeChatFromCurrentUser(chatId);
                return;
            }

            // Refetch so other users immediately see new members / renamed group.
            queryClient.invalidateQueries({ queryKey: CHAT_LIST_QUERY_KEY, refetchType: 'active' });
            const activeChatId = getActiveChatIdFromPath();
            if (activeChatId && String(activeChatId) === String(chatId)) {
                queryClient.invalidateQueries({ queryKey: CHAT_QUERY_KEY(chatId), refetchType: 'active' });
            }
        };

        const onGroupExited = (payload) => {
            const chatId = payload?.chatId ?? payload?.id ?? null;
            if (!chatId) return;
            removeChatFromCurrentUser(chatId);
        };

        s.on('chat:inbox:new', onInboxNew);
        s.on('chat:unread:updated', onUnreadUpdated);
        s.on('chat:read:updated', onReadUpdated);
        s.on('chat:cleared', onChatCleared);
        s.on('chat:group:renamed', onGroupChanged);
        s.on('chat:group:members:added', onGroupChanged);
        s.on('chat:group:member:removed', onGroupChanged);
        s.on('chat:group:member:left', onGroupChanged);
        s.on('chat:group:exited', onGroupExited);
        s.on('chat:group:created', onGroupChanged);
        // Admin ownership transfer events.
        s.on('chat:group:admin:change', onGroupChanged);
        s.on('chat:group:admin:assigned', onGroupChanged);

        return () => {
            s.off('chat:inbox:new', onInboxNew);
            s.off('chat:unread:updated', onUnreadUpdated);
            s.off('chat:read:updated', onReadUpdated);
            s.off('chat:cleared', onChatCleared);
            s.off('chat:group:renamed', onGroupChanged);
            s.off('chat:group:members:added', onGroupChanged);
            s.off('chat:group:member:removed', onGroupChanged);
            s.off('chat:group:member:left', onGroupChanged);
            s.off('chat:group:exited', onGroupExited);
            s.off('chat:group:created', onGroupChanged);
            s.off('chat:group:admin:change', onGroupChanged);
            s.off('chat:group:admin:assigned', onGroupChanged);
        };
    }, [queryClient]);
}
